import { useFocusEffect } from 'expo-router'
import { useCallback, useRef, type RefObject } from 'react'
import { AccessibilityInfo, View, type NativeMethods } from 'react-native'

export const useFocusableRef = <T extends NativeMethods = View>(
  autoFocus?: boolean
): [RefObject<T | null>, (accessibilityFocus?: boolean, componentFocus?: boolean) => void] => {
  const ref = useRef<T>(null)
  const maybeFocus = useCallback((accessibilityFocus: boolean = true, componentFocus: boolean = true) => {
    if (ref.current) {
      // since focusing a component might trigger some sort of UI event, such as opening the keyboard or a bottomsheet,
      // we allow optionally skipping component focus
      if (componentFocus && typeof ref.current.focus === 'function') {
        ref.current.focus()
      }

      // conversely, we might want to trigger component focus, but not accessibility focus
      if (accessibilityFocus && typeof AccessibilityInfo.sendAccessibilityEvent === 'function') {
        AccessibilityInfo.sendAccessibilityEvent(ref.current, 'focus')
      }
    }
  }, [])

  useFocusEffect(
    useCallback(() => {
      if (autoFocus) {
        const timeout = setTimeout(maybeFocus, 0)

        return () => clearTimeout(timeout)
      }
    }, [autoFocus, maybeFocus])
  )

  return [ref, maybeFocus]
}
